import { useEffect } from 'react'

/**
 * Publishes the part of the screen the browser is actually showing, as two CSS
 * variables on the root element, and keeps a focused field inside it.
 *
 * A `position: fixed` element is placed against the *layout* viewport, and an
 * on-screen keyboard does not change that one — it only shrinks the *visual*
 * viewport and draws itself over the rest. So a sheet pinned to `bottom: 0`
 * sits behind the keyboard, with the fields the visitor is typing into hidden
 * under it. Nothing in CSS exposes that on its own; `visualViewport` does.
 * This is the one place in the app that measures it.
 *
 * `--viewport-height`  what is visible, in px. `vh` is the *large* viewport —
 *                      the page as if the address bar were hidden — so a
 *                      surface sized in it reaches above the top of what is on
 *                      screen and loses its own heading off the top edge.
 * `--viewport-inset-bottom`
 *                      how much is covered at the bottom: the keyboard, or a
 *                      browser widget. Zero when nothing is.
 *
 * Installed once at the app root: one listener for a document-level fact, and
 * the result is CSS, so what reads it stays in CSS. Android also gets the
 * layout viewport resized by `interactive-widget=resizes-content` in
 * `index.html`; iOS does not, and this is what covers it there.
 */
export function useViewportBounds() {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const root = document.documentElement

    // How tall the box a `position: fixed` element is placed in actually is,
    // measured. `window.innerHeight` answers a neighbouring question — on iOS
    // it follows the visible area as the toolbars collapse — and a bottom
    // offset computed from it puts the sheet behind the keyboard or floating
    // above it. This is the same edge a sheet's own `bottom` is measured from,
    // so the two cannot disagree.
    const probe = document.createElement('div')
    probe.style.cssText =
      'position:fixed;top:0;bottom:0;width:0;visibility:hidden;pointer-events:none'
    document.body.append(probe)

    let layout = probe.getBoundingClientRect().height
    let height = -1
    let covered = -1

    const publish = () => {
      // Writing a custom property on the root invalidates style for every
      // element under it, and neither number moves during an ordinary scroll.
      const nextHeight = viewport.height
      if (nextHeight !== height) {
        height = nextHeight
        root.style.setProperty('--viewport-height', `${nextHeight}px`)
      }
      // `offsetTop` is how far the visible area sits below the top of that
      // box, so what is covered at its foot is whatever the two leave.
      const nextCovered = Math.max(0, layout - viewport.offsetTop - nextHeight)
      if (nextCovered !== covered) {
        covered = nextCovered
        root.style.setProperty('--viewport-inset-bottom', `${nextCovered}px`)
      }
    }

    const measure = () => {
      // Read before anything is written: a custom property on the root dirties
      // the whole document's style, and a rect asked for afterwards has to
      // flush that synchronously.
      layout = probe.getBoundingClientRect().height
      const shrunk = viewport.height < height
      publish()

      // The surface the field sits on has just been made shorter, which can
      // leave the field the visitor is typing into below the part of it still
      // on screen. The browser scrolls a field into view when it takes focus,
      // not when what holds it is resized afterwards.
      if (shrunk) {
        document.activeElement
          ?.closest('input,textarea,select')
          ?.scrollIntoView({ block: 'nearest' })
      }
    }

    publish()
    viewport.addEventListener('resize', measure)
    // Only `offsetTop` moves on a scroll, and that costs no layout to read.
    viewport.addEventListener('scroll', publish)

    return () => {
      viewport.removeEventListener('resize', measure)
      viewport.removeEventListener('scroll', publish)
      root.style.removeProperty('--viewport-height')
      root.style.removeProperty('--viewport-inset-bottom')
      probe.remove()
    }
  }, [])
}
